/* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions, no-unused-vars */
import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import grapesjs from 'grapesjs';
import 'grapesjs/dist/css/grapes.min.css';
import { 
  FaArrowLeft, 
  FaEye, 
  FaDesktop, 
  FaTabletAlt, 
  FaMobileAlt, 
  FaUndo, 
  FaRedo, 
  FaTrash, 
  FaCheck 
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import axiosInstance from '../apis/axiosConfig';
import './ModifyBlogContent.css'; // Share the exact CSS file to guarantee identical styling!

const ModifyPageContent = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [pageTitle, setPageTitle] = useState('Loading Page...');
  const [pageData, setPageData] = useState(null);
  const [editor, setEditor] = useState(null);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [activeSidebarTab, setActiveSidebarTab] = useState('blocks'); // 'blocks', 'styles', 'traits'
  const [activeDevice, setActiveDevice] = useState('desktop'); // 'desktop', 'tablet', 'mobile'
  
  const editorRef = useRef(null);

  useEffect(() => {
    fetchPageData();
  }, [id]);

  const fetchPageData = async () => {
    try {
      const res = await axiosInstance.get(`/pages/privacy`);
      if (res && res.data && res.data.success) {
        const p = res.data.data;
        setPageData(p);
        setPageTitle(p.page_heading || 'Privacy Policy');
      } else {
        toast.error('Page record not found');
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to load page content');
    }
  };

  useEffect(() => {
    if (!pageData) return;
    if (!editorRef.current) return;

    const initialContent = pageData.description || '';

    // Initialize GrapesJS
    const gjsEditor = grapesjs.init({
      container: editorRef.current,
      fromElement: false,
      height: '100%',
      width: '100%',
      storageManager: false,
      panels: { defaults: [] }, // Custom outer controls
      deviceManager: {
        devices: [
          {
            id: 'desktop',
            name: 'Desktop',
            width: '100%',
          },
          {
            id: 'tablet',
            name: 'Tablet',
            width: '768px',
          },
          {
            id: 'mobile',
            name: 'Mobile portrait',
            width: '375px',
          }
        ]
      },
      blockManager: {
        appendTo: '#gjs-blocks-container',
        blocks: [
          {
            id: 'heading',
            label: `<div class="gjs-custom-block-content"><i class="fa-solid fa-heading block-icon"></i><div class="block-label">Heading</div></div>`,
            category: 'Base',
            content: '<h2 style="font-family: \'Poppins\', sans-serif; font-weight: 600; color: #c4a882; margin-bottom: 1rem;">Type your heading here</h2>',
            attributes: { class: 'gjs-block-custom-heading' }
          },
          {
            id: 'paragraph',
            label: `<div class="gjs-custom-block-content"><i class="fa-solid fa-paragraph block-icon"></i><div class="block-label">Paragraph</div></div>`,
            category: 'Base',
            content: '<p style="font-family: \'Poppins\', sans-serif; line-height: 1.6; color: #e5e5e5; margin-bottom: 1rem;">Our customers are of paramount importance to us & we greatly value the faith our customers have in us. Type paragraph here...</p>',
          },
          {
            id: 'image',
            label: `<div class="gjs-custom-block-content"><i class="fa-regular fa-image block-icon"></i><div class="block-label">Image</div></div>`,
            category: 'Base',
            content: { type: 'image' },
          },
          {
            id: 'blockquote',
            label: `<div class="gjs-custom-block-content"><i class="fa-solid fa-quote-right block-icon"></i><div class="block-label">Blockquote</div></div>`,
            category: 'Base',
            content: '<blockquote style="border-left: 4px solid #c4a882; padding-left: 1.5rem; margin: 1.5rem 0; font-style: italic; color: #a0a0a0; font-family: \'Poppins\', sans-serif;">This is a blockquote. Edit this text.</blockquote>',
          },
          {
            id: 'horizontal-rule',
            label: `<div class="gjs-custom-block-content"><i class="fa-solid fa-minus block-icon"></i><div class="block-label">Horizontal Rule</div></div>`,
            category: 'Base',
            content: '<hr style="border: 0; border-top: 1px solid rgba(196, 168, 130, 0.25); margin: 2rem 0;"/>',
          },
          {
            id: 'link',
            label: `<div class="gjs-custom-block-content"><i class="fa-solid fa-link block-icon"></i><div class="block-label">Link</div></div>`,
            category: 'Base',
            content: '<a href="#" style="color: #c4a882; text-decoration: underline;">Link Text</a>',
          },
          {
            id: 'list',
            label: `<div class="gjs-custom-block-content"><i class="fa-solid fa-list block-icon"></i><div class="block-label">List</div></div>`,
            category: 'Base',
            content: '<ul style="margin-bottom: 1rem; padding-left: 1.5rem; color: #e5e5e5;"><li style="margin-bottom: 0.5rem;">List item 1</li><li style="margin-bottom: 0.5rem;">List item 2</li></ul>',
          }
        ]
      },
      assetManager: {
        upload: `${process.env.REACT_APP_BACKEND || (process.env.REACT_APP_BACKEND_URL && process.env.REACT_APP_BACKEND_URL.split('/api')[0]) || 'http://localhost:8000'}/api/v1/products/upload`,
        uploadName: 'image',
        params: {},
        headers: {},
        autoAdd: true,
        uploadResponse: (response) => {
          const backendHost = (process.env.REACT_APP_BACKEND || (process.env.REACT_APP_BACKEND_URL && process.env.REACT_APP_BACKEND_URL.split('/api')[0]) || 'http://localhost:8000').replace(/\/$/, '');
          const fileUrl = `${backendHost}/uploads/${response.filename}`;
          return {
            data: [fileUrl]
          };
        }
      },
      styleManager: {
        appendTo: '#gjs-style-container',
        sectors: [
          {
            name: 'General Layout',
            open: true,
            buildProps: ['display', 'position', 'top', 'right', 'left', 'bottom', 'float']
          },
          {
            name: 'Spacing & Size',
            open: true,
            buildProps: ['width', 'height', 'max-width', 'margin', 'padding']
          },
          {
            name: 'Typography',
            open: true,
            buildProps: ['font-family', 'font-size', 'font-weight', 'color', 'line-height', 'text-align']
          },
          {
            name: 'Decorations',
            open: false,
            buildProps: ['background-color', 'border', 'border-radius', 'box-shadow']
          }
        ]
      },
      traitManager: {
        appendTo: '#gjs-trait-container'
      },
      canvas: {
        styles: [
          'https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600&display=swap',
          'body, html { background-color: #121211 !important; margin: 0 !important; padding: 0 !important; height: 100% !important; width: 100% !important; font-family: "Poppins", sans-serif; }',
          '#wrapper, [data-gjs-type="wrapper"] { background-color: #121211 !important; color: #e5e5e5 !important; padding: 2rem 3rem !important; max-width: 100% !important; width: 100% !important; margin: 0 !important; min-height: 100vh !important; box-shadow: none !important; border-radius: 0 !important; }',
          '* { background-color: transparent !important; color: #e5e5e5 !important; }',
          'h1, h2, h3, h4, h5, h6, b, strong { color: #c4a882 !important; }',
          'a { color: #c4a882 !important; text-decoration: underline; }',
          'blockquote { color: #a0a0a0 !important; border-left: 4px solid #c4a882 !important; background-color: rgba(196, 168, 130, 0.05) !important; padding: 1rem !important; }'
        ]
      }
    });

    const cleanHtmlContent = (html) => {
      if (!html) return '';
      let cleaned = html.replace(/<link[^>]*stylesheet[^>]*>/gi, '');
      cleaned = cleaned.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');
      const bodyMatch = cleaned.match(/<body[^>]*>([\s\S]*)<\/body>/i);
      if (bodyMatch && bodyMatch[1]) {
        return bodyMatch[1].trim();
      }
      return cleaned.replace(/<\/?(html|head|body)[^>]*>/gi, '').trim();
    };

    if (initialContent) {
      gjsEditor.setComponents(cleanHtmlContent(initialContent));
    }

    setEditor(gjsEditor);

    gjsEditor.on('load', () => {
      const css = gjsEditor.Css;
      if (css) {
        css.setRule('#wrapper, .gjs-wrapper, [data-gjs-type="wrapper"]', {
          'background-color': '#121211 !important',
          color: '#e5e5e5 !important',
          padding: '2.5rem 6% !important',
          width: '100% !important',
          'max-width': '1100px !important',
          margin: '2rem auto !important',
          'min-height': 'calc(100vh - 120px) !important',
          'box-shadow': '0 10px 40px rgba(0, 0, 0, 0.6) !important',
          'border-radius': '8px !important',
          'box-sizing': 'border-box !important',
          'text-align': 'left !important'
        });

        css.setRule('body, html', {
          'background-color': '#080807 !important',
          margin: '0 !important',
          padding: '0 !important',
          width: '100% !important',
          height: '100% !important',
          'text-align': 'left !important'
        });

        css.setRule('span[id^="docs-internal-guid-"]', {
          display: 'block !important',
          width: '100% !important',
          'text-align': 'left !important'
        });

        css.setRule('p, span, li, td, th, strong, em, b, i, font, a', {
          color: '#e5e5e5 !important',
          'font-size': '1.05rem !important',
          'line-height': '1.7 !important',
          'text-align': 'left !important'
        });

        css.setRule('h1, h2, h3, h4, h5, h6, h1 *, h2 *, h3 *, h4 *, h5 *, h6 *', {
          color: '#c4a882 !important',
          'font-size': '1.75rem !important',
          'text-align': 'left !important'
        });

        css.setRule('blockquote', {
          color: '#a0a0a0 !important',
          'border-left': '4px solid #c4a882 !important',
          'background-color': 'rgba(196, 168, 130, 0.05) !important',
          padding: '1.25rem !important',
          margin: '1.5rem 0 !important',
          'text-align': 'left !important'
        });

        css.setRule('[class*="editor-width-"], .editor-margin-auto, .col-md-12, .row, .container, .container-fluid', {
          width: '100% !important',
          'max-width': '100% !important',
          margin: '0 !important',
          padding: '0 !important',
          'background-color': 'transparent !important',
          'text-align': 'left !important'
        });

        css.setRule('#wrapper > *, .gjs-wrapper > *, [data-gjs-type="wrapper"] > *', {
          width: '100% !important',
          'max-width': '100% !important',
          'margin-left': '0 !important',
          'margin-right': '0 !important',
          'text-align': 'left !important'
        });

        css.setRule('img', {
          'max-width': '100% !important',
          height: 'auto !important',
          display: 'block !important',
          margin: '1.5rem 0 !important'
        });
      }
    });

    gjsEditor.on('component:selected', () => {
      setActiveSidebarTab('styles');
    });

    gjsEditor.on('run:preview:before', () => {
      setIsPreviewMode(true);
    });
    gjsEditor.on('stop:preview:before', () => {
      setIsPreviewMode(false);
    });

    return () => {
      if (gjsEditor) {
        gjsEditor.destroy();
      }
    };
  }, [pageData]);

  // Editor Actions
  const handleDeviceChange = (device) => {
    if (editor) {
      editor.setDevice(device);
      setActiveDevice(device);
    }
  };

  const handleUndo = () => editor && editor.UndoManager.undo();
  const handleRedo = () => editor && editor.UndoManager.redo();
  const handleClearCanvas = () => {
    if (editor && window.confirm("Are you sure you want to clear the canvas content?")) {
      editor.setComponents('');
    }
  };

  const handlePreview = () => {
    if (editor) {
      if (editor.Commands.isActive('preview')) {
        editor.stopCommand('preview');
      } else {
        editor.runCommand('preview');
      }
    }
  };

  const handleViewPage = () => {
    if (!editor) return;
    const html = editor.getHtml();
    const css = editor.getCss();
    
    const previewHtml = `
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8">
        <title>${pageTitle} - Preview</title>
        <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600&display=swap" rel="stylesheet">
        <style>
          body {
            font-family: 'Poppins', sans-serif;
            background-color: #ffffff;
            color: #222222;
            margin: 0;
            padding: 3rem 2rem;
            max-width: 1000px;
            margin: 0 auto;
            line-height: 1.6;
          }
          img {
            max-width: 100%;
            height: auto;
            display: block;
            margin: 1.5rem auto;
            border-radius: 8px;
          }
          ${css}
        </style>
      </head>
      <body>
        ${html}
      </body>
      </html>
    `;
    
    const newWindow = window.open();
    if (newWindow) {
      newWindow.document.open();
      newWindow.document.write(previewHtml);
      newWindow.document.close();
    } else {
      toast.error("Popup blocked! Please allow popups to view the page.");
    }
  };

  const handlePublish = async () => {
    if (!editor || !pageData) return;

    const html = editor.getHtml();
    const css = editor.getCss();
    const compiledContent = `<style>${css}</style>${html}`;

    try {
      const payload = {
        page_heading: pageData.page_heading,
        short_description: pageData.short_description,
        description: compiledContent,
        status: pageData.status,
        banner: pageData.banner,
        banner_status: pageData.banner_status,
        meta_title: pageData.meta_title,
        meta_description: pageData.meta_description,
        meta_keywords: pageData.meta_keywords
      };

      const res = await axiosInstance.patch('/pages/privacy', payload);
      if (res.data && res.data.success) {
        toast.success('Page content published successfully!');
        navigate('/page-list');
      } else {
        toast.error(res.data.message || 'Failed to publish page content');
      }
    } catch (err) {
      console.error(err);
      toast.error('Error publishing page content');
    }
  };

  return (
    <div className="builder-page-wrapper animate-fade-up">
      {/* Top Breadcrumb Navigation */}
      <div className="builder-breadcrumbs">
        <span onClick={() => navigate('/dashboard')} className="clickable">Dashboard</span> / <span onClick={() => navigate('/page-list')} className="clickable">Page Manager</span> / <span className="active">{pageTitle} Content</span>
      </div>

      {/* Main Panel Header */}
      <div className="builder-header-panel">
        {/* Left Side: History Controls */}
        <div className="header-left-group">
          <div className="toolbar-group">
            <button className="toolbar-btn" type="button" onClick={handleUndo} title="Undo">
              <FaUndo />
            </button>
            <button className="toolbar-btn" type="button" onClick={handleRedo} title="Redo">
              <FaRedo />
            </button>
            <button className="toolbar-btn text-danger" type="button" onClick={handleClearCanvas} title="Clear Canvas">
              <FaTrash />
            </button>
          </div>
          {/* Preview action */}
          <button className="toolbar-btn" type="button" onClick={handlePreview} title="Live Preview">
            <FaEye style={{ marginRight: '6px' }} /> Preview
          </button>
        </div>

        {/* Center: Device Viewports & View Page */}
        <div className="header-center-group">
          <button className="btn-view-page" type="button" onClick={handleViewPage} title="View Published Page">
            <i className="fa-solid fa-arrow-up-right-from-square" style={{ marginRight: '6px' }}></i> View page
          </button>
          <div className="toolbar-group">
            <button 
              className={`toolbar-btn ${activeDevice === 'mobile' ? 'active' : ''}`} 
              type="button" 
              onClick={() => handleDeviceChange('mobile')} 
              title="Mobile view"
            >
              <FaMobileAlt />
            </button>
            <button 
              className={`toolbar-btn ${activeDevice === 'tablet' ? 'active' : ''}`} 
              type="button" 
              onClick={() => handleDeviceChange('tablet')} 
              title="Tablet view"
            >
              <FaTabletAlt />
            </button>
            <button 
              className={`toolbar-btn ${activeDevice === 'desktop' ? 'active' : ''}`} 
              type="button" 
              onClick={() => handleDeviceChange('desktop')} 
              title="Desktop view"
            >
              <FaDesktop />
            </button>
          </div>
        </div>

        {/* Right: Publish Button */}
        <div className="publish-actions-row">
          <button className="btn-publish" type="button" onClick={handlePublish}>
            <FaCheck style={{ marginRight: '8px' }} /> Publish Now
          </button>
        </div>
      </div>

      {/* Workspace Grid */}
      <div className="builder-editor-container">
        {/* Sidebar blocks column */}
        {!isPreviewMode && (
          <div className="builder-sidebar-blocks">
            <div className="sidebar-tabs-header">
              <div 
                className={`tab-item ${activeSidebarTab === 'blocks' ? 'active' : ''}`}
                onClick={() => setActiveSidebarTab('blocks')}
              >
                Blocks
              </div>
              <div 
                className={`tab-item ${activeSidebarTab === 'styles' ? 'active' : ''}`}
                onClick={() => setActiveSidebarTab('styles')}
              >
                Styles
              </div>
              <div 
                className={`tab-item ${activeSidebarTab === 'traits' ? 'active' : ''}`}
                onClick={() => setActiveSidebarTab('traits')}
              >
                Settings
              </div>
            </div>
            
            <div className="sidebar-blocks-content">
              {/* Blocks selector */}
              <div style={{ display: activeSidebarTab === 'blocks' ? 'block' : 'none' }}>
                <small className="blocks-section-title">Drag & Drop Blocks</small>
                <div id="gjs-blocks-container"></div>
              </div>
              
              {/* Styles manager */}
              <div style={{ display: activeSidebarTab === 'styles' ? 'block' : 'none' }}>
                <small className="blocks-section-title">Element Styles</small>
                <div id="gjs-style-container"></div>
              </div>
              
              {/* Settings / Trait manager */}
              <div style={{ display: activeSidebarTab === 'traits' ? 'block' : 'none' }}>
                <small className="blocks-section-title">Element Settings</small>
                <div id="gjs-trait-container"></div>
              </div>
            </div>
          </div>
        )}

        {/* Canvas editor container */}
        <div className="builder-canvas-viewport">
          <div ref={editorRef} id="gjs"></div>
        </div>
      </div>
    </div>
  );
};

export default ModifyPageContent;
